/**
 * Link Registry for centralized management of API endpoints
 * This registry dynamically updates with HATEOAS links and provides
 * fallback to hardcoded endpoints when necessary.
 */

// Base API endpoints that serve as fallbacks when HATEOAS links aren't available
const API_ENDPOINTS = {
  // Exam related endpoints
  EXAMS: '/api/exam',
  EXAM_BY_ID: (id) => `/api/exam/${id}`,
  EXAM_START: '/api/exam/start',
  EXAM_END: '/api/exam/end',
  EXAM_QUESTIONS: (id) => `/api/exam/${id}/questions`,
  EXAM_RESPONSES: (id) => `/api/exam/${id}/responses`,
  EXAM_ANSWER: (examId, questionId) => `/api/exam/${examId}/question/${questionId}`,
  EXAM_PERFORMANCE: (id) => `/api/exam/${id}/performance`,
  
  // Question related endpoints
  QUESTION_BY_ID: (id) => `/api/question/${id}`,
  QUESTION_ANSWERS: (id) => `/api/question/${id}/answers`,
  
  // Exam type related endpoints
  EXAM_TYPES: '/api/examtype',
  EXAM_TYPE_BY_ID: (id) => `/api/examtype/${id}`,
  
  // Category related endpoints
  CATEGORY_GROUPS: '/api/categorygroup',
  CATEGORY_GROUPS_BY_EXAM_TYPE: (examTypeId) => `/api/categorygroup?ExamTypeId=${examTypeId}`,
  CATEGORIES: '/api/category',
  CATEGORIES_BY_GROUP: (groupId, enabled) => `/api/category?CategoryGroupId=${groupId}&Enabled=${enabled}`,
  
  // Other endpoints
  FEEDBACK: '/api/feedback',
  AI_ASSIST: '/api/ai/assist',
};

// Dynamic registry that gets populated during API interactions
const dynamicLinks = new Map();

/**
 * Registers a HATEOAS link for a resource
 * @param {string} resourceType - Type of resource (e.g., 'exam', 'question')
 * @param {string|number} resourceId - ID of the resource
 * @param {string} rel - The relation name
 * @param {string} href - The link URL
 */
export const registerLink = (resourceType, resourceId, rel, href) => {
  const key = `${resourceType}:${resourceId}:${rel}`;
  dynamicLinks.set(key, href);
};

/**
 * Registers all links for a resource
 * @param {string} resourceType - Type of resource
 * @param {string|number} resourceId - ID of the resource
 * @param {Object} links - Object containing rel:href pairs
 */
export const registerLinks = (resourceType, resourceId, links) => {
  if (!links) return;
  
  Object.entries(links).forEach(([rel, href]) => {
    if (typeof href === 'object' && href.href) {
      registerLink(resourceType, resourceId, rel, href.href);
    } else if (typeof href === 'string') {
      registerLink(resourceType, resourceId, rel, href);
    }
  });
};

/**
 * Gets a link for a resource, preferring dynamic links but falling back to hardcoded endpoints
 * @param {string} resourceType - Type of resource
 * @param {string|number} resourceId - ID of the resource
 * @param {string} rel - The relation name
 * @returns {string|null} - The link URL or null if not found
 */
export const getResourceLink = (resourceType, resourceId, rel) => {
  const key = `${resourceType}:${resourceId}:${rel}`;
  
  // First check if we have a dynamic link
  if (dynamicLinks.has(key)) {
    return dynamicLinks.get(key);
  }
  
  // Fall back to hardcoded endpoints based on resource type and relation
  switch (resourceType) {
    case 'exam':
      switch (rel) {
        case 'self': return API_ENDPOINTS.EXAM_BY_ID(resourceId);
        case 'questions': return API_ENDPOINTS.EXAM_QUESTIONS(resourceId);
        case 'responses': return API_ENDPOINTS.EXAM_RESPONSES(resourceId);
        case 'performance': return API_ENDPOINTS.EXAM_PERFORMANCE(resourceId);
        default: return null;
      }
      
    case 'question':
      switch (rel) {
        case 'self': return API_ENDPOINTS.QUESTION_BY_ID(resourceId);
        case 'answers': return API_ENDPOINTS.QUESTION_ANSWERS(resourceId);
        default: return null;
      }
      
    case 'examType':
      switch (rel) {
        case 'self': return API_ENDPOINTS.EXAM_TYPE_BY_ID(resourceId);
        default: return null;
      }
      
    default:
      return null;
  }
};

/**
 * Extracts and registers all links from a resource
 * @param {Object} resource - The resource with _links property
 * @param {string} resourceType - Type of resource
 */
export const extractAndRegisterLinks = (resource, resourceType) => {
  if (!resource || !resource.id) return;
  
  // Register standard HATEOAS _links
  if (resource._links) {
    Object.entries(resource._links).forEach(([rel, linkObj]) => {
      if (linkObj && linkObj.href) {
        registerLink(resourceType, resource.id, rel, linkObj.href);
      }
    });
  }
  
  // Register any URL properties that look like links
  Object.entries(resource).forEach(([key, value]) => {
    if ((key.endsWith('Url') || key.endsWith('Link')) && typeof value === 'string') {
      const rel = key.replace(/Url$/, '').replace(/Link$/, '');
      registerLink(resourceType, resource.id, rel, value);
    }
  });
};

export default API_ENDPOINTS;
